//Common
import {
  CAMPO_CONTRATO,
  SITUACAO_REAJUSTE,
  TIPO_PERIOD_REAJUSTE,
} from "@/common/constantes";
import { fn_getSituacaoContrato } from "@/common/siig";
import {
  fn_addMonth,
  fn_dataIsAfter,
  fn_dataIsBefore,
  fn_getDifDatasSort,
  fn_getMesAnoData,
  fn_validarData,
} from "@/common/utils/date";
import {
  fn_capitalizarPalavra,
  fn_validarEmail,
  fn_validarTelefone,
} from "@/common/utils/util";

//Util
import { fn_parseOptions } from "../util";
import { MENSAGENS_ERRO, showMensagemErro } from "../mensagens";

//Retorna a situação do contrato para apresentar na tela
export function fn_getJsxSituacaoContrato(situacao) {
  const objSituacao = fn_getSituacaoContrato(situacao);

  if (objSituacao) {
    return <span className={objSituacao.className}>{objSituacao.label}</span>;
  }
}

//Retorna um array com os campos do contrato agrupados pelo grupo do campo
export function fn_montarCamposContratoPorGrupo(dadosContrato) {
  const gruposCampos = dadosContrato.reduce((acc, cur) => {
    const campo = cur.campo_contrato;

    const grupo = campo.campo_contrato_grupo;

    if (!acc[grupo.id]) {
      acc[grupo.id] = {
        ...grupo,
        campos: [],
      };
    }

    const objCampo = {
      ...campo,
      valor: cur.valor,
      descricao_valor: cur.descricao_valor,
    };

    acc[grupo.id].campos.push(objCampo);
    return acc;
  }, {});

  return Object.values(gruposCampos);
}

//Retorna os valores de cada campo do contrato de acordo com o tipo do campo
export async function fn_getValoresCamposContrato(body, camposContrato) {
  const valoresCampos = {};

  for (const campo of camposContrato) {
    const valor =
      campo.tipo_campo == "DATA_PERIODO"
        ? body[campo.nome + "_inicio"] + " - " + body[campo.nome + "_fim"]
        : campo.tipo_campo == "CHECK"
          ? body[campo.nome] === true
            ? "S"
            : "N"
          : body[campo.nome];

    if (valor != undefined && valor != null && valor != "") {
      let descValor = null;

      if (campo.tipo_campo == "SEL" || campo.tipo_campo == "RADIO") {
        const opcoes = await fn_parseOptions(campo.opcoes);
        const opcao = opcoes.find((op) => op.value == valor);

        descValor = opcao.label;
      } else if (campo.tipo_campo == "CHECK") {
        descValor = valor === "S" ? "Sim" : "Não";
      }

      valoresCampos[campo.id] = {
        id_campo_contrato: campo.id,
        valor: valor,
        descricao_valor: descValor,
      };
    }
  }

  return valoresCampos;
}

//Retorna as opções do campo do contrato que são apresentadas na tela:
//as ativas e a que está gravada no contrato, mesmo que esteja inativa (opção sem situação é considerada ativa)
export function fn_getOpcoesApresentarCampoContrato(
  opcoes = [],
  valorGravado = null,
) {
  return opcoes.filter(
    (opcao) =>
      opcao.situacao != "I" ||
      (valorGravado != null && opcao.value == valorGravado),
  );
}

//Retorna os campos não obrigatorios do contrato
export function fn_getCamposNaoObrigatoriosContrato(formRef, camposContrato) {
  return camposContrato
    .filter((campo) => {
      const isDisabled =
        formRef.current?.getFieldRef(campo.nome)?.current?.disabled ||
        formRef.current?.getFieldRef(campo.nome)?.props?.isDisabled;

      return campo.obrigatorio == "N" || isDisabled;
    })
    .map((campo) => campo.nome);
}

//Retorna a qtde de colunas por linha
export function fn_parseLayoutColunas(colunas) {
  if (!colunas) return [1];

  // se for só número → padrão antigo
  if (!String(colunas).includes("-")) {
    return [Number(colunas)];
  }

  return String(colunas)
    .split("-")
    .map((n) => Number(n))
    .filter((n) => n > 0);
}

//Valida determinado campo do contrato de acordo com o tipo do campo e tipo de valor
export function fn_validarCampoContrato(
  campo,
  nomeCampo,
  valorCampo,
  formRef,
  showMessage = true,
) {
  let msgErro = null;
  let param1Msg = null;

  //Se o campo possuir valor mínimo
  if (valorCampo && campo.valor_minimo != null) {
    if (Number(valorCampo) < Number(campo.valor_minimo)) {
      msgErro = MENSAGENS_ERRO.VALOR_MINIMO_PERMITIDO;
      param1Msg = campo.valor_minimo;
    }
  }
  //Se o campo possuir valor máximo
  else if (valorCampo && campo.valor_maximo != null) {
    if (Number(valorCampo) > Number(campo.valor_maximo)) {
      msgErro = MENSAGENS_ERRO.VALOR_MAXIMO_PERMITIDO;
      param1Msg = campo.valor_maximo;
    }
  }

  //Se o tipo do campo for email
  else if (campo.tipo_campo == "EMAIL") {
    if (valorCampo && !fn_validarEmail(valorCampo)) {
      msgErro = MENSAGENS_ERRO.INVALIDO;
      param1Msg = "E-mail";
    }
  }
  //Se o tipo do campo for campo telefone
  else if (campo.tipo_campo == "TEL") {
    if (valorCampo && !fn_validarTelefone(valorCampo)) {
      msgErro = MENSAGENS_ERRO.INVALIDO;
      param1Msg = "Telefone";
    }
  }
  //Se o tipo do campo for data
  else if (campo.tipo_campo.startsWith("DATA")) {
    if (valorCampo && !fn_validarData(valorCampo)) {
      msgErro = MENSAGENS_ERRO.DATA_INVALIDA;
    }

    //Se for campo de período de data
    if (nomeCampo.includes("_fim")) {
      const dataInicio = formRef.current.getFieldValue(campo.nome + "_inicio");

      if (dataInicio && valorCampo && fn_dataIsBefore(valorCampo, dataInicio)) {
        msgErro = MENSAGENS_ERRO.DATA_FIM_POSTERIOR_OU_IGUAL_DATA_INICIO;
      }
    }
  }

  if (showMessage) {
    showMensagemErro(msgErro, param1Msg);
  }

  return msgErro ? false : true;
}

//Valida todos os campos do formulário do contrato
export function fn_validarCamposContratoForm(formRef, camposContrato) {
  let formIsValido = true;
  let errors = {};

  const body = formRef.current.getData();

  Object.entries(body).forEach(([nomeCampo, valorCampo]) => {
    const nomeBase = nomeCampo.replace(/_inicio$/, "").replace(/_fim$/, "");
    const campoFind = camposContrato.find((campo) => campo.nome == nomeBase);

    if (campoFind) {
      const campoIsValido = fn_validarCampoContrato(
        campoFind,
        nomeCampo,
        valorCampo,
        formRef,
        false, //não apresentar msg de erro
      );

      if (!campoIsValido) {
        errors[nomeCampo] = "Inválido";
        formIsValido = false;
      }
    }
  });

  if (!formIsValido) {
    formRef.current.setErrors(errors);
  }
  return formIsValido;
}

/*
 * Retorna o mês/ano do próximo reajuste do contrato: o início do primeiro período
 * (depois do primeiro, que não é reajustado) que ainda não possui reajuste aprovado.
 * REGRA (a mesma dos ciclos de reajuste - fn_gerarCiclosReajusteContrato):
 *  - Os períodos iniciam no início da vigência e seguem a periodicidade do contrato.
 *  - Considera os períodos até o fim da vigência ou até o mês do último pagamento, o que for maior,
 *    pois os pagamentos posteriores ao fim da vigência também são reajustados.
 * @param mesAnoUltimoPag: Mês/ano ref. (MM/YYYY) do último pagamento. Se não informar, considera só a vigência.
 */
export function fn_getMesAnoProximoReajuste(
  contrato,
  reajustes = [],
  mesAnoUltimoPag = null,
) {
  //Se o contrato não for reajustável sai da função
  if (!contrato.isReajustavel()) return null;

  const [dataInicio, dataFim] = contrato.vigencia.split(" - ");

  const tipo = contrato.tipo_periodicidade;

  const valor =
    contrato.tipo_periodicidade_intervalo || contrato.tipo_periodicidade_mes;

  // =========================================================
  // HELPERS
  // =========================================================
  /**
   * Busca reajuste solicitado para o período
   * Considera mes_ano_inicio do período
   */
  function getReajustePeriodo(mesAnoInicio) {
    return reajustes.find((r) => r?.mes_ano_inicio == mesAnoInicio);
  }

  /**
   * Verifica se reajuste está aprovado
   */
  function reajusteAprovado(reajuste) {
    return reajuste?.situacao == SITUACAO_REAJUSTE.APROVADO;
  }

  /**
   * Verifica se a data está dentro do período em que o contrato é reajustado:
   * até o fim da vigência ou até o mês do último pagamento
   */
  function dentroPeriodoReajuste(data) {
    if (fn_dataIsBefore(data, dataFim, true, "DD/MM/YYYY")) {
      return true;
    }

    return mesAnoUltimoPag
      ? fn_dataIsBefore(fn_getMesAnoData(data), mesAnoUltimoPag, true, "MM/YYYY")
      : false;
  }

  // =========================================================
  // PERIODICIDADE POR INTERVALO
  // =========================================================
  if (tipo === TIPO_PERIOD_REAJUSTE.INTERVALO_MESES) {
    const intervalo = Number(valor);

    let inicioPeriodo = dataInicio;

    let indicePeriodo = 0;

    while (dentroPeriodoReajuste(inicioPeriodo)) {
      const mesAnoInicio = fn_getMesAnoData(inicioPeriodo);

      // =====================================================
      // Período 0 = NÃO reajustável
      // =====================================================

      if (indicePeriodo > 0) {
        const reajuste = getReajustePeriodo(mesAnoInicio);

        // Não possui solicitação
        if (!reajuste) {
          return mesAnoInicio;
        }

        // Possui mas não aprovado
        if (!reajusteAprovado(reajuste)) {
          return mesAnoInicio;
        }
      }

      inicioPeriodo = fn_addMonth(inicioPeriodo, intervalo);

      indicePeriodo++;
    }

    return null;
  }

  // =========================================================
  // PERIODICIDADE POR DATA BASE
  // =========================================================
  if (tipo === TIPO_PERIOD_REAJUSTE.DATA_BASE) {
    const mesBase = Number(valor);

    let atual = dataInicio;

    let indicePeriodo = 0;

    while (dentroPeriodoReajuste(atual)) {
      const inicioPeriodo = atual;

      const mesAnoInicio = fn_getMesAnoData(inicioPeriodo);

      // =====================================================
      // Período 0 = NÃO reajustável
      // =====================================================

      if (indicePeriodo > 0) {
        const reajuste = getReajustePeriodo(mesAnoInicio);

        // Não possui solicitação
        if (!reajuste) {
          return mesAnoInicio;
        }

        // Possui mas não aprovado
        if (!reajusteAprovado(reajuste)) {
          return mesAnoInicio;
        }
      }

      let cursor = atual;

      while (dentroPeriodoReajuste(cursor)) {
        const proximoMes = fn_addMonth(cursor, 1);

        const mesProximo = Number(proximoMes.split("/")[1]);

        if (mesProximo === mesBase) {
          break;
        }

        cursor = proximoMes;
      }

      atual = fn_addMonth(cursor, 1);

      indicePeriodo++;
    }

    return null;
  }

  throw new Error(
    "Tipo periodicidade reajuste inválido! Valores válidos: 'I' ou 'D'",
  );
}

//Retorna a descrição do valor da periodicidade conforme o tipo
export function fn_getDescPeriodicidadeReajuste(contrato) {
  //   //Se tipo periodicidade data base (mês)
  //   if (contrato.tipo_periodicidade_mes?.descricao) {
  //     return fn_capitalizarPalavra(contrato.tipo_periodicidade_mes.descricao);
  //   }
  //   //Se tipo periodicidade intervlo meses
  //   else if (contrato.tipo_periodicidade_intervalo?.descricao) {
  //     return `${contrato.tipo_periodicidade_intervalo.descricao} meses`;
  //   } else {
  //     return "";
  //   }

  //Se tipo periodicidade data base (mês)
  if (contrato.tipo_periodicidade_mes) {
    return fn_capitalizarPalavra(contrato.tipo_periodicidade_mes_desc);
  }

  //Se tipo periodicidade intervalo meses
  if (contrato.tipo_periodicidade_intervalo) {
    return `${contrato.tipo_periodicidade_intervalo} meses`;
  }

  return "";
}

/*
 * Gera os ciclos (períodos) de reajuste do contrato com os pagamentos de cada mês.
 * REGRA:
 *  - O primeiro ciclo inicia no mês de início da vigência do contrato e não é reajustado.
 *  - Os ciclos seguintes são gerados conforme a periodicidade (intervalo de meses ou mês da data base).
 *  - O último ciclo termina no mês do último pagamento, mesmo que seja posterior ao fim da vigência.
 *  - Não existe grupo "fora da vigência": pagamento com mês/ano ref. anterior ao início da vigência
 *    fica no primeiro ciclo (sem reajuste).
 * OBS: Os ciclos são controlados por mês/ano. O dia de início da vigência não é considerado.
 */
export function fn_gerarCiclosReajusteContrato(
  contrato,
  pagamentos = [],
  indices = [],
  reajustesSol = {}, //nao obrigatorio
) {
  const [dataInicio] = contrato.vigencia.split(" - ");

  const tipo = contrato.tipo_periodicidade;

  const valorPeriodicidade =
    contrato.tipo_periodicidade_intervalo || contrato.tipo_periodicidade_mes;

  //Os ciclos são controlados por mês: utiliza o dia 1 do mês de início da vigência
  //para as comparações não dependerem do dia em que a vigência inicia
  const mesAnoInicioVigencia = fn_getMesAnoData(dataInicio);
  let atual = "01/" + mesAnoInicioVigencia;

  //O último ciclo termina no mês do último pagamento (maior mês/ano ref.), mesmo que seja posterior ao fim da vigência
  const ultimoPag = pagamentos.at(-1);
  let dataFimCiclo = "01/" + ultimoPag.mes_ano_ref;

  pagamentos.forEach((pag) => {
    if (
      pag?.mes_ano_ref &&
      fn_dataIsAfter("01/" + pag.mes_ano_ref, dataFimCiclo, false, "DD/MM/YYYY")
    ) {
      dataFimCiclo = "01/" + pag.mes_ano_ref;
    }
  });

  const resultado = [];

  // MAPA DE ÍNDICES
  const mapaIndices = {};

  indices.forEach((ind) => {
    if (ind?.mes_ano) {
      mapaIndices[ind.mes_ano.trim()] = {
        tipo: ind.tipo,
        percentual: Number(ind.percentual || 0),
      };
    }
  });

  function getIndice(mesAno) {
    return mapaIndices[mesAno] || null;
  }

  // MAPA DE PAGAMENTOS POR MÊS/ANO
  const mapaPagamentos = {};

  pagamentos.forEach((pag) => {
    if (!pag?.data_emissao) return;

    const mesAno = pag.mes_ano_ref;

    if (!mapaPagamentos[mesAno]) {
      mapaPagamentos[mesAno] = [];
    }

    mapaPagamentos[mesAno].push(pag);
  });

  // Controle de meses utilizados
  const mesesUsados = new Set();

  function getPagamentosMes(mesAno) {
    mesesUsados.add(mesAno);

    return mapaPagamentos[mesAno] || [];
  }

  // PERIODICIDADE POR INTERVALO
  if (tipo === TIPO_PERIOD_REAJUSTE.INTERVALO_MESES) {
    const intervalo = Number(valorPeriodicidade);

    let numReajuste = 0;

    while (fn_dataIsBefore(atual, dataFimCiclo, true, "DD/MM/YYYY")) {
      const limite = fn_addMonth(atual, intervalo);

      const mesAnoInicio = fn_getMesAnoData(atual);

      const ultimoMesCiclo = fn_dataIsBefore(
        limite,
        dataFimCiclo,
        false,
        "DD/MM/YYYY",
      )
        ? fn_addMonth(limite, -1)
        : dataFimCiclo;

      const ciclo = {
        numero_reajuste: numReajuste,
        reajuste: reajustesSol[numReajuste],
        mes_ano_inicio: mesAnoInicio,
        mes_ano_fim: fn_getMesAnoData(ultimoMesCiclo),
        meses: [],
      };

      let cursor = atual;

      while (
        fn_dataIsBefore(cursor, limite, false, "DD/MM/YYYY") &&
        fn_dataIsBefore(cursor, dataFimCiclo, true, "DD/MM/YYYY")
      ) {
        const mesAnoRef = fn_getMesAnoData(cursor);

        ciclo.meses.push({
          mes_ano_ref: mesAnoRef,
          pagamentos: getPagamentosMes(mesAnoRef),
          indice: getIndice(mesAnoRef),
        });

        cursor = fn_addMonth(cursor, 1);
      }

      resultado.push(ciclo);

      atual = limite;
      numReajuste++;
    }
  }

  // PERIODICIDADE POR DATA BASE
  else if (tipo === TIPO_PERIOD_REAJUSTE.DATA_BASE) {
    const mesBase = Number(valorPeriodicidade);

    let numReajuste = 0;

    while (fn_dataIsBefore(atual, dataFimCiclo, true, "DD/MM/YYYY")) {
      const ciclo = {
        numero_reajuste: numReajuste,
        reajuste: reajustesSol[numReajuste],
        mes_ano_inicio: fn_getMesAnoData(atual),
        mes_ano_fim: null,
        meses: [],
      };

      let cursor = atual;

      while (fn_dataIsBefore(cursor, dataFimCiclo, true, "DD/MM/YYYY")) {
        const mesAnoRef = fn_getMesAnoData(cursor);

        ciclo.meses.push({
          mes_ano_ref: mesAnoRef,
          pagamentos: getPagamentosMes(mesAnoRef),
          indice: getIndice(mesAnoRef),
        });

        const proximoMes = fn_addMonth(cursor, 1);

        const mesProximo = Number(proximoMes.split("/")[1]);

        if (mesProximo === mesBase) {
          break;
        }

        cursor = proximoMes;
      }

      ciclo.mes_ano_fim =
        ciclo.meses[ciclo.meses.length - 1]?.mes_ano_ref || null;

      resultado.push(ciclo);

      atual = fn_addMonth(cursor, 1);
      numReajuste++;
    }
  } else {
    throw new Error(
      "Tipo periodicidade reajuste inválido! Valores válidos: 'I' ou 'D'",
    );
  }

  // PAGAMENTOS COM MÊS/ANO REF. ANTERIOR AO INÍCIO DA VIGÊNCIA
  //Ficam no primeiro ciclo (sem reajuste), antes dos meses da vigência, para não deixarem de ser apresentados
  const mesesAnteriores = Object.keys(mapaPagamentos)
    .filter((mesAno) => !mesesUsados.has(mesAno))
    .sort((a, b) => fn_getDifDatasSort(a, b, "MM/YYYY"));

  if (mesesAnteriores.length > 0) {
    //Se todos os pagamentos forem anteriores à vigência, ainda não existe ciclo
    if (resultado.length == 0) {
      resultado.push({
        numero_reajuste: 0,
        reajuste: reajustesSol[0],
        mes_ano_inicio: mesAnoInicioVigencia,
        mes_ano_fim: mesAnoInicioVigencia,
        meses: [],
      });
    }

    resultado[0].meses.unshift(
      ...mesesAnteriores.map((mesAno) => ({
        mes_ano_ref: mesAno,
        pagamentos: getPagamentosMes(mesAno),
        indice: getIndice(mesAno),
      })),
    );
  }

  return resultado;
}

//Retorna as opções de filtro da tela gestora de contrato
export function fn_loadOpcoesCampoFiltroContrato() {
  let ordem = 0;
  const opcoes = [
    //{ value: "", label: "Nenhum" },
    {
      value: "id_empresa",
      label: "Empresa",
      tipo_campo: "SEL",
      ordem: ordem++,
    },
    {
      value: "id_fornecedor",
      label: "Fornecedor",
      tipo_campo: "SEL_ASYNC",
      ordem: ordem++,
    },
    {
      value: "id_usuario",
      label: "Cadastrado por",
      tipo_campo: "SEL_ASYNC",
      ordem: ordem++,
    },
    {
      value: "nome_negociador",
      label: "Negociador",
      tipo_campo: "TXT",
      ordem: ordem++,
    },

    //Filtros dos campos do contrato (tabela solicitacao_contrato)
    {
      value: CAMPO_CONTRATO.ACORDO.nome,
      label: "Acordo",
      id_campo_contrato: CAMPO_CONTRATO.ACORDO.id,
      tipo_campo: "SEL",
      ordem: ordem++,
    },

    {
      value: CAMPO_CONTRATO.INDICE_REAJUSTE.nome,
      label: "Índice reajuste",
      id_campo_contrato: CAMPO_CONTRATO.INDICE_REAJUSTE.id,
      tipo_campo: "SEL",
      ordem: ordem++,
    },

    {
      value: CAMPO_CONTRATO.TIPO_PERIODICIDADE_MES.nome,
      label: "Period. reajuste: mês",
      id_campo_contrato: CAMPO_CONTRATO.TIPO_PERIODICIDADE_MES.id,
      tipo_campo: "SEL",
      ordem: ordem++,
    },

    {
      value: CAMPO_CONTRATO.TIPO_PERIODICIDADE_INTERVALO.nome,
      label: "Period. reajuste: intervalo meses",
      id_campo_contrato: CAMPO_CONTRATO.TIPO_PERIODICIDADE_INTERVALO.id,
      tipo_campo: "TXT",
      ordem: ordem++,
    },
  ];

  return opcoes;
}
